import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { ArrowLeft, TrendingUp, CheckCircle, ChevronRight, ArrowRight } from "lucide-react";

const COLOUR_MAP: Record<string, { badge: string; ring: string; header: string; bullet: string }> = {
  emerald: { badge: "bg-emerald-100 text-emerald-800 border-emerald-200", ring: "border-emerald-400", header: "text-emerald-800", bullet: "bg-emerald-400" },
  teal:    { badge: "bg-teal-100 text-teal-800 border-teal-200",         ring: "border-teal-400",    header: "text-teal-800",    bullet: "bg-teal-400" },
  blue:    { badge: "bg-blue-100 text-blue-800 border-blue-200",         ring: "border-blue-400",    header: "text-blue-800",    bullet: "bg-blue-400" },
  indigo:  { badge: "bg-indigo-100 text-indigo-800 border-indigo-200",   ring: "border-indigo-400",  header: "text-indigo-800",  bullet: "bg-indigo-400" },
  purple:  { badge: "bg-purple-100 text-purple-800 border-purple-200",   ring: "border-purple-400",  header: "text-purple-800",  bullet: "bg-purple-400" },
  rose:    { badge: "bg-rose-100 text-rose-800 border-rose-200",         ring: "border-rose-400",    header: "text-rose-800",    bullet: "bg-rose-400" },
};

const BANDS = [
  {
    band: "Band 5",
    title: "Staff Nurse · Newly Qualified",
    salary: "£29,970 – £36,483",
    duration: "Typically 1–2 years",
    colour: "emerald",
    competencies: [
      "Complete trust rheumatology induction and mandatory competencies",
      "Develop core assessment and drug monitoring skills under supervision",
      "Manage routine monitoring clinic caseloads (MTX, HCQ blood tests)",
      "Engage with NMC revalidation from day one — start CPD log",
      "Begin building familiarity with NICE/BSR drug monitoring guidelines",
    ],
    development: [
      "Identify a clinical supervisor in the rheumatology team",
      "Join the RCN Rheumatology Nursing Forum (student/affiliate rate)",
      "Complete trust-mandated competency sign-offs for drug monitoring",
      "Attend departmental teaching and relevant BSR webinars",
      "Start a reflective practice journal for NMC revalidation",
    ],
    transition: "Demonstrate autonomous management of a routine monitoring caseload, evidenced supervision skills, and initiation of a CPD portfolio.",
  },
  {
    band: "Band 6",
    title: "Senior Staff Nurse · Developing Specialist",
    salary: "£37,338 – £44,962",
    duration: "Typically 2–4 years",
    colour: "teal",
    competencies: [
      "Manage a complex, autonomous drug monitoring and advice line caseload",
      "Run or contribute to nurse-led DMARD monitoring clinics",
      "Educate and counsel patients starting new biologic or DMARD therapies",
      "Supervise Band 5 staff and student nurses",
      "Contribute to departmental audit and service improvement",
    ],
    development: [
      "Obtain RCN Rheumatology Nursing Forum full membership",
      "Complete a post-registration module in rheumatology or long-term conditions",
      "Attend BSR annual conference and relevant EULAR educational sessions",
      "Develop skills in DAS28 / RAPID3 scoring and treat-to-target principles",
      "Build NMC revalidation portfolio: 5 practice-related CPD pieces, 5 reflective accounts",
    ],
    transition: "Evidence advanced clinical reasoning, leadership of a specialist nurse service component, and formal supervision of others.",
  },
  {
    band: "Band 7",
    title: "Clinical Nurse Specialist (CNS) · Senior Specialist",
    salary: "£46,148 – £52,809",
    duration: "Typically 3–6 years",
    colour: "blue",
    competencies: [
      "Expert-level clinical reasoning; independently manage complex biologic prescribing support",
      "Lead nurse-led clinics (annual reviews, new patient education, pre-biologic assessments)",
      "Act as primary point of contact for rheumatology advice line",
      "Provide formal clinical supervision and act as preceptor",
      "Drive service development, clinical audit, and governance",
    ],
    development: [
      "Pursue an MSc module or full MSc in Advanced Practice / Clinical Specialisms",
      "Consider independent nurse prescribing qualification (V300) — see ANP/ACP pathway",
      "Obtain BSR Rheumatology Nursing Credential or equivalent RCN qualification",
      "Contribute to or lead a departmental QI project",
      "Join BSR Nurse, AHP and Scientist committee or equivalent",
    ],
    transition: "Evidence system-level service leadership, postgraduate qualification, mentoring of junior staff, and ability to act autonomously across the full CNS role.",
  },
  {
    band: "Band 8a",
    title: "Advanced Nurse Practitioner (ANP) · Lead CNS",
    salary: "£53,755 – £60,504",
    duration: "Typically 4–8 years",
    colour: "indigo",
    competencies: [
      "Independent Nurse Prescribing qualification (V300) — prescribe biologics, DMARDs, steroids",
      "Manage a complex advanced caseload including undifferentiated presentations",
      "Hold or contribute to a clinical leadership portfolio (service lead, governance lead)",
      "Provide consultant-level clinical support to junior nursing staff and MDT",
      "Lead departmental educational programmes and CPD provision",
    ],
    development: [
      "Achieve NHS England ACP credential (4-pillar framework — clinical, leadership, education, research)",
      "Complete full MSc in Advanced Practice if not already held",
      "Build a research portfolio — contribute to departmental audit, service evaluation, or RCT support",
      "Engage with national networks: BHPR, BSR, RCN Rheumatology",
      "Explore joint working and extended scope with consultant rheumatologists",
    ],
    transition: "Meet the NHS England four-pillar ACP credential requirements; evidence senior leadership, prescribing competence, and system-wide impact.",
  },
  {
    band: "Band 8b",
    title: "Consultant Nurse in Rheumatology",
    salary: "£62,215 – £72,293",
    duration: "Established senior role",
    colour: "purple",
    competencies: [
      "Expert-level clinical practice across the full rheumatology specialty",
      "Strategic leadership of nurse-led services at departmental and trust level",
      "Research and innovation — lead or co-lead clinical research projects",
      "Education leadership — design and deliver postgraduate nurse training",
      "Professional leadership — contribute to national policy and guideline development (BSR, RCN, NHSE)",
    ],
    development: [
      "Seek Consultant Nurse job plan agreement: 50% clinical + 50% leadership/education/research",
      "Engage with RCN Nurse Consultant network and BSR Nurse Consultant group",
      "Contribute to EULAR and BSR guideline committees",
      "Build a publication record and conference presentation portfolio",
      "Mentor future ANPs and CNSs across the region or nationally",
    ],
    transition: "Typically a competitive appointment process. Build a strong portfolio across all four NHS Consultant Nurse pillars before applying.",
  },
  {
    band: "Band 8c/8d",
    title: "Head of Nursing / Associate Director",
    salary: "£74,290 – £98,956",
    duration: "Senior leadership appointment",
    colour: "rose",
    competencies: [
      "Strategic clinical leadership across a division or specialty portfolio",
      "Workforce planning, budget management, and service transformation",
      "Board-level nursing representation and quality oversight",
      "External stakeholder engagement with NHS England, ICBs, royal colleges",
      "Drive research and innovation strategy at trust or system level",
    ],
    development: [
      "Complete senior NHS leadership programme (GMTS, FMLM Fellow, FNF Fellowship)",
      "Build executive-level relationships across divisional and trust leadership",
      "Pursue Fellowship of the Royal College of Nursing (FRCN) if appropriate",
      "Engage with NHS Confederation, NHS Providers, and ICB nursing networks",
    ],
    transition: "Senior executive appointment, typically following demonstrated divisional leadership and service transformation impact.",
  },
];

export default function NurseBandProgression() {
  return (
    <NurseLayout title="NHS Band Progression">
      <Link href="/nurse/career">
        <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Career Hub
        </button>
      </Link>

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-teal-100 p-2.5 rounded-xl">
            <TrendingUp className="h-6 w-6 text-teal-700" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">NHS Band Progression</h2>
            <p className="text-sm text-slate-500">Rheumatology Nursing · Agenda for Change · NMC Registered</p>
          </div>
        </div>
        <p className="text-slate-600 text-sm leading-relaxed max-w-3xl">
          The NHS Agenda for Change (AfC) framework underpins all nursing career progression. Below is the typical pathway for UK rheumatology specialist nurses, from newly qualified Band 5 through to Consultant Nurse and senior leadership roles. Salary ranges are indicative AfC rates (England, 2024/25 — excluding London weighting).
        </p>
      </div>

      {/* Band progression steps */}
      <div className="space-y-6">
        {BANDS.map((b, idx) => {
          const c = COLOUR_MAP[b.colour] ?? COLOUR_MAP.teal;
          return (
            <div key={b.band} className={`bg-white rounded-2xl border-2 ${c.ring} shadow-sm overflow-hidden`}>
              <div className="p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-bold px-3 py-1 rounded-full border ${c.badge}`}>{b.band}</span>
                    <div>
                      <h3 className={`font-bold text-lg ${c.header}`}>{b.title}</h3>
                      <p className="text-xs text-slate-500">{b.duration} · {b.salary}</p>
                    </div>
                  </div>
                  {idx < BANDS.length - 1 && (
                    <ArrowRight className="h-5 w-5 text-slate-300 shrink-0 hidden md:block mt-1" />
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Key Competencies</p>
                    <ul className="space-y-2">
                      {b.competencies.map((comp, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle className={`h-4 w-4 mt-0.5 shrink-0 ${c.header}`} />
                          {comp}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Development Focus</p>
                    <ul className="space-y-2">
                      {b.development.map((d, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className={`h-1.5 w-1.5 rounded-full mt-2 shrink-0 ${c.bullet}`} />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {b.transition && (
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                      <ChevronRight className="inline h-3 w-3 mr-1" />Progression Requirement
                    </p>
                    <p className="text-sm text-slate-600 italic">{b.transition}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 bg-slate-900 text-white rounded-2xl p-6">
        <h3 className="font-bold mb-2">AfC & Job Evaluation</h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          All NHS bands are determined through NHS Job Evaluation (JE) using the NHS Job Evaluation Handbook. If you believe your role has changed significantly, request a formal job evaluation review via your line manager or HR department. Always cross-reference with your trust's pay scales — London weighting adds 20% for Inner London, 15% Outer London, 5% Fringe.
        </p>
      </div>
    </NurseLayout>
  );
}
